<!--
  The player of a recording (a voice note / an audio file) in a chat bubble. The browser's own
  controls looked foreign in the bubble (the owner, 15/10/2026: "the voice player ui is not nice,
  use something custom and good"): a round play / pause button, a bar that seeks on a click or a
  drag, the length (the position while playing / paused) and a speed pill (1x / 1.5x / 2x). One
  recording plays at a time. A browser that cannot decode the file (Safari with whatsapp's
  ogg/opus) gets a note with a link to the file instead. The <audio> element stays in the DOM,
  hidden, with its chatMessageAudio class (the tests find a recording by it).
-->
<template>
    <div class="chatVoice"
         :class="{ playing: playing, chatVoiceFailed: failed }">

        <template v-if="!failed">

            <button type="button"
                    class="chatVoiceBtn"
                    :title="playing ? 'השהיה' : 'ניגון'"
                    @click="toggle">
                <i :class="playing ? 'fa-solid fa-pause' : 'fa-solid fa-play'"></i>
            </button>

            <div class="chatVoiceBody">

                <div class="chatVoiceTrack"
                     ref="track"
                     @pointerdown="onPointerDown">
                    <div class="chatVoiceFill"
                         :style="{ width: progress + '%' }"></div>
                    <div class="chatVoiceKnob"
                         :style="{ left: progress + '%' }"></div>
                </div>

                <div class="chatVoiceMeta">

                    <span class="chatVoiceTime">{{ timeLabel }}</span>

                    <button type="button"
                            v-if="playing || rate != 1"
                            class="chatVoiceRate"
                            title="מהירות הניגון"
                            @click="cycleRate">{{ rate }}x</button>

                </div>

            </div>

        </template>

        <a v-else
           class="chatVoiceFallback"
           :href="src"
           target="_blank">
            <i class="fa-solid fa-microphone"></i>
            הדפדפן לא מנגן את ההקלטה - לפתיחת הקובץ
        </a>

        <audio ref="audio"
               class="chatMessageAudio"
               :src="src"
               preload="metadata"
               @loadedmetadata="onMeta"
               @durationchange="onMeta"
               @timeupdate="onTime"
               @play="onPlay"
               @pause="onPause"
               @ended="onEnded"
               @error="onError"></audio>

    </div>
</template>
<script>

//the recording that plays now - starting another one pauses it
let nowPlaying = null;

const RATES = [1, 1.5, 2];

//1:05
function formatSeconds(seconds) {

    if (!isFinite(seconds) || seconds < 0)
        seconds = 0;

    let whole = Math.floor(seconds);

    return Math.floor(whole / 60) + ':' + (whole % 60 < 10 ? '0' : '') + (whole % 60);
}

export default {

    name: 'ChatVoicePlayer',

    props: ['src'],

    data() {
        return {
            playing: false,
            failed: false,
            duration: 0,
            currentTime: 0,
            rate: 1,
            dragging: false
        }
    },

    computed: {

        progress() {

            if (!this.duration)
                return 0;

            return Math.min(100, Math.max(0, this.currentTime / this.duration * 100));
        },

        //the length until it plays; the position while it plays or sits paused in the middle
        timeLabel() {

            return formatSeconds(this.playing || this.currentTime > 0 ? this.currentTime : this.duration);
        }
    },

    beforeDestroy() {

        if (nowPlaying === this)
            nowPlaying = null;
    },

    methods: {

        toggle() {

            let audio = this.$refs.audio;

            if (!audio)
                return;

            if (this.playing) {
                audio.pause();
                return;
            }

            if (nowPlaying && nowPlaying !== this)
                nowPlaying.pause();

            audio.playbackRate = this.rate;

            let started = audio.play();

            //a file the browser cannot decode rejects here (or fires error)
            if (started && started.catch)
                started.catch(() => { this.failed = true; });
        },

        pause() {

            if (this.$refs.audio)
                this.$refs.audio.pause();
        },

        onMeta() {

            let duration = this.$refs.audio ? this.$refs.audio.duration : 0;

            this.duration = isFinite(duration) ? duration : 0;
        },

        onTime() {

            if (!this.dragging && this.$refs.audio)
                this.currentTime = this.$refs.audio.currentTime;
        },

        onPlay() {
            this.playing = true;
            nowPlaying = this;
        },

        onPause() {

            this.playing = false;

            if (nowPlaying === this)
                nowPlaying = null;
        },

        //back to the start, so the label shows the length again
        onEnded() {

            this.playing = false;
            this.currentTime = 0;

            try { this.$refs.audio.currentTime = 0; } catch (e) { }
        },

        onError() {
            this.failed = true;
        },

        cycleRate() {

            this.rate = RATES[(RATES.indexOf(this.rate) + 1) % RATES.length];

            if (this.$refs.audio)
                this.$refs.audio.playbackRate = this.rate;
        },

        //a click or a drag on the bar seeks (the bar is laid out left to right)
        onPointerDown(event) {

            let track = this.$refs.track;

            if (!track || !this.duration)
                return;

            event.preventDefault();

            this.dragging = true;

            let seekTo = ev => {

                let rect = track.getBoundingClientRect();
                let ratio = rect.width ? (ev.clientX - rect.left) / rect.width : 0;

                this.currentTime = Math.min(1, Math.max(0, ratio)) * this.duration;
            };

            let move = ev => seekTo(ev);

            let up = ev => {

                seekTo(ev);

                this.dragging = false;

                try { this.$refs.audio.currentTime = this.currentTime; } catch (e) { }

                document.removeEventListener('pointermove', move);
                document.removeEventListener('pointerup', up);
                document.removeEventListener('pointercancel', up);
            };

            seekTo(event);

            document.addEventListener('pointermove', move);
            document.addEventListener('pointerup', up);
            document.addEventListener('pointercancel', up);
        }
    }
}
</script>

<style>
.chatVoice {
    display: flex;
    align-items: center;
    gap: 10px;
    direction: ltr;
    width: 270px;
    max-width: 100%;
    min-height: 44px;
    margin: 2px 0 4px;
}

.chatVoice audio {
    display: none;
}

.chatVoiceBtn {
    flex: none;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    border: none;
    background: #7d5fc4;
    color: #fff;
    font-size: 15px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 0;
    box-shadow: 0 4px 10px rgba(125, 95, 196, .3);
    transition: background .15s, transform .1s;
}

.chatVoiceBtn:hover {
    background: #6b4fb3;
}

.chatVoiceBtn:active {
    transform: scale(.95);
}

/* the play glyph sits left of its box's center */
.chatVoiceBtn .fa-play {
    position: relative;
    left: 2px;
}

.chatVoiceBody {
    flex: 1 1 auto;
    min-width: 0;
}

.chatVoiceTrack {
    position: relative;
    height: 18px;
    cursor: pointer;
    touch-action: none;
}

.chatVoiceTrack::before {
    content: "";
    position: absolute;
    left: 0;
    right: 0;
    top: 8px;
    height: 3px;
    border-radius: 2px;
    background: rgba(42, 49, 66, .15);
}

.chatVoiceFill {
    position: absolute;
    left: 0;
    top: 8px;
    height: 3px;
    border-radius: 2px;
    background: #7d5fc4;
}

.chatVoiceKnob {
    position: absolute;
    top: 3px;
    width: 13px;
    height: 13px;
    margin-left: -6px;
    border-radius: 50%;
    background: #7d5fc4;
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, .25);
    transition: transform .1s;
}

.chatVoice.playing .chatVoiceKnob {
    transform: scale(1.15);
}

.chatVoiceMeta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 1px;
    font-size: 11px;
    line-height: 1.3;
    color: #6b7280;
    min-height: 16px;
}

.chatVoiceTime {
    font-variant-numeric: tabular-nums;
}

.chatVoiceRate {
    border: none;
    background: rgba(125, 95, 196, .12);
    color: #6d5bd0;
    font-size: 10px;
    font-weight: 600;
    border-radius: 999px;
    padding: 1px 7px;
    line-height: 1.5;
    cursor: pointer;
}

.chatVoiceRate:hover {
    background: rgba(125, 95, 196, .22);
}

.chatVoiceFallback {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #6d5bd0;
    direction: rtl;
}
</style>
